import React, { useEffect, useState, useCallback } from 'react';
import { Container, Row, Col, Button, Table, Pagination, Form } from 'react-bootstrap';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { View, ViewOff, TrashCan, RequestQuote } from '@carbon/icons-react';
import Swal from 'sweetalert2';
import FlavorService from '../services/FlavorService';
import FlavorCategoryModal from '../components/flavors/FlavorCategoryModal';
import FlavorModal from '../components/flavors/FlavorModal';
import './FlavorManagement.css';

const FlavorManagement = () => {
    // Estados para categorías
    const [categories, setCategories] = useState([]);
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [categoryCurrentPage, setCategoryCurrentPage] = useState(1);
    const [categoryTotalPages, setCategoryTotalPages] = useState(0);
    const [categoryCurrentNumberOfElements, setCategoryCurrentNumberOfElements] = useState(0);
    const [categoryTotalElements, setCategoryTotalElements] = useState(0);

    // Estados para gustos
    const [flavors, setFlavors] = useState([]);
    const [flavorSearchTerm, setFlavorSearchTerm] = useState('');
    const [flavorCurrentPage, setFlavorCurrentPage] = useState(1);
    const [flavorTotalPages, setFlavorTotalPages] = useState(0);
    const [flavorCurrentNumberOfElements, setFlavorCurrentNumberOfElements] = useState(0);
    const [flavorTotalElements, setFlavorTotalElements] = useState(0);
    const [selectedCategoryId, setSelectedCategoryId] = useState('');

    // Estado de carga
    const [isLoading, setIsLoading] = useState(false);
    
    // Memoizar las funciones de carga para evitar recreaciones innecesarias
    const loadCategories = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await FlavorService.getFlavorCategoriesPage(
                categoryCurrentPage - 1,
                10,
                'displayOrder',
                'ASC',
                { name: categorySearchTerm }
            );
            setCategories(response.data.content);
            setCategoryTotalPages(response.data.totalPages);
            setCategoryCurrentPage(response.data.number + 1);
            setCategoryCurrentNumberOfElements(response.data.numberOfElements);
            setCategoryTotalElements(response.data.totalElements);
        } catch (error) {
            console.error('Error al cargar categorías:', error);
            alertError('Error al cargar las categorías');
        } finally {
            setIsLoading(false);
        }
    }, [categoryCurrentPage, categorySearchTerm]);

    const loadFlavors = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await FlavorService.getFlavorsPage(
                flavorCurrentPage - 1,
                10,
                'name',
                'ASC',
                {
                    name: flavorSearchTerm,
                    ...(selectedCategoryId && { flavorCategoryId: selectedCategoryId })
                }
            );
            setFlavors(response.data.content);
            setFlavorTotalPages(response.data.totalPages);
            setFlavorCurrentPage(response.data.number + 1);
            setFlavorCurrentNumberOfElements(response.data.numberOfElements);
            setFlavorTotalElements(response.data.totalElements);
        } catch (error) {
            console.error('Error al cargar gustos:', error);
            alertError('Error al cargar los gustos');
        } finally {
            setIsLoading(false);
        }
    }, [flavorCurrentPage, flavorSearchTerm, selectedCategoryId]);

    // Estados para modales
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showFlavorModal, setShowFlavorModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedFlavor, setSelectedFlavor] = useState(null);

    // Efectos para cargar datos
    useEffect(() => {
        loadCategories();
    }, [categoryCurrentPage, categorySearchTerm, loadCategories]);

    useEffect(() => {
        loadFlavors();
    }, [flavorCurrentPage, flavorSearchTerm, selectedCategoryId, loadFlavors]);

    // Funciones de utilidad
    const alertError = (mensaje) => {
        Swal.fire({
            title: 'Error',
            text: mensaje,
            icon: 'error',
            showConfirmButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false
        });
    };

    const alertSuccess = (mensaje) => {
        Swal.fire({
            icon: 'success',
            title: '¡Éxito!',
            text: mensaje,
            timer: 1500,
            showConfirmButton: false,
        });
    };

    // Funciones para manejar categorías
    const handleCategorySearch = () => {
        setCategoryCurrentPage(1);
        loadCategories();
    };

    const handleCategoryClear = () => {
        setCategorySearchTerm('');
        setCategoryCurrentPage(1);
    };

    const handleCategoryPageChange = (page) => {
        setCategoryCurrentPage(page);
    };

    const handleDeleteCategory = async (category) => {
        Swal.fire({
            title: '¿Estás seguro de eliminar esta categoría?',
            text: 'Esta acción no se puede deshacer y eliminará todos los gustos asociados.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            confirmButtonColor: 'rgb(246, 139, 127)',
            cancelButtonText: 'Cancelar',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await FlavorService.deleteFlavorCategory(category.id);
                    alertSuccess('Categoría eliminada correctamente');
                    loadCategories();
                } catch (error) {
                    alertError(error.response?.data?.errorMsg || 'Error al eliminar la categoría');
                }
            }
        });
    };

    const toggleCategoryStatus = async (category) => {
        try {
            await FlavorService.toggleFlavorCategoryStatus(category.id, !category.status);
            alertSuccess('Categoría ' + (category.status ? 'desactivada' : 'activada') + ' correctamente');
            loadCategories();
        } catch (error) {
            alertError(error.response?.data?.errorMsg || 'Error al ' + (category.status ? 'desactivar' : 'activar') + ' la categoría');
        }
    };

    // Funciones para manejar gustos
    const handleFlavorSearch = () => {
        setFlavorCurrentPage(1);
        loadFlavors();
    };

    const handleFlavorClear = () => {
        setFlavorSearchTerm('');
        setFlavorCurrentPage(1);
    };

    const handleFlavorPageChange = (page) => {
        setFlavorCurrentPage(page);
    };

    const handleDeleteFlavor = async (flavor) => {
        Swal.fire({
            title: '¿Estás seguro de eliminar este gusto?',
            text: 'Esta acción no se puede deshacer.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            confirmButtonColor: 'rgb(246, 139, 127)',
            cancelButtonText: 'Cancelar',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await FlavorService.deleteFlavor(flavor.id);
                    alertSuccess('Gusto eliminado correctamente');
                    loadFlavors();
                } catch (error) {
                    alertError(error.response?.data?.errorMsg || 'Error al eliminar el gusto');
                }
            }
        });
    };

    const toggleFlavorStatus = async (flavor) => {
        try {
            await FlavorService.toggleFlavorStatus(flavor.id, !flavor.status);
            alertSuccess('Gusto ' + (flavor.status ? 'desactivado' : 'activado') + ' correctamente');
            loadFlavors();
        } catch (error) {
            alertError(error.response?.data?.errorMsg || 'Error al ' + (flavor.status ? 'desactivar' : 'activar') + ' el gusto');
        }
    };

    return (
        <div className="flavorManagementContainer">
            {isLoading && (
                <div className="text-center my-3">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            )}
            <Container className="mt-3">
                <Row>
                    <Col>
                        <h1 className="titleProducts">Gestión de Gustos</h1>
                    </Col>
                </Row>
            </Container>

            {/* Sección de Categorías */}
            <Container className="mt-4">
                <Row className="mb-3">
                    <Col>
                        <h2 className="h4">Categorías de Gustos</h2>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <input
                            type="text"
                            className="searchTerm"
                            placeholder="Buscar categoría..."
                            value={categorySearchTerm}
                            onChange={(e) => setCategorySearchTerm(e.target.value)}
                        />
                    </Col>
                    <Col md={6} className="text-end">
                        <Button variant="primary" className="me-2" onClick={handleCategorySearch}>
                            <FaSearch />
                        </Button>
                        <Button variant="primary" onClick={handleCategoryClear}>
                            <FaTimes />
                        </Button>
                    </Col>
                </Row>

                <div className="table-responsive mt-3">
                    <Table responsive className="table-striped">
                        <thead>
                            <tr className="trTable">
                                <th className="text-center fw-bold" style={{
                                    backgroundColor: "rgb(235, 120, 107)",
                                    width: "40%",
                                    color: "#ffffff"
                                }}>Nombre</th>
                                <th className="text-center fw-bold" style={{
                                    backgroundColor: "rgb(235, 120, 107)",
                                    width: "25%",
                                    color: "#ffffff"
                                }}>Orden</th>
                                <th className="text-center fw-bold" style={{
                                    backgroundColor: "rgb(235, 120, 107)",
                                    width: "20%",
                                    color: "#ffffff"
                                }}>Estado</th>
                                <th className="text-center fw-bold" style={{
                                    backgroundColor: "rgb(235, 120, 107)",
                                    width: "15%",
                                    color: "#ffffff"
                                }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categories.map((category) => (
                                <tr key={category.id}>
                                    <td className="text-center">{category.name}</td>
                                    <td className="text-center">{category.displayOrder}</td>
                                    <td className="text-center">{category.status ? 'Activo' : 'Inactivo'}</td>
                                    <td>
                                        <div className="d-flex justify-content-center">
                                            <button
                                                className="btn btnEdit me-2"
                                                onClick={() => {
                                                    setSelectedCategory(category);
                                                    setShowCategoryModal(true);
                                                }}
                                            >
                                                <RequestQuote size="24"/>
                                            </button>
                                            <button
                                                className="btn btnActiveInactive me-2"
                                                onClick={() => toggleCategoryStatus(category)}
                                            >
                                                {category.status ? (
                                                    <View size="24"/>
                                                ) : (
                                                    <ViewOff size="24"/>
                                                )}
                                            </button>
                                            <button
                                                className="btn btnDelete"
                                                onClick={() => handleDeleteCategory(category)}
                                            >
                                                <TrashCan size="24"/>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>

                <Container fluid>
                    <Row>
                        <Col className="d-flex align-items-center justify-content-between">
                            <Pagination>
                                <Pagination.First
                                    onClick={() => handleCategoryPageChange(1)}
                                    disabled={categoryCurrentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => handleCategoryPageChange(categoryCurrentPage - 1)}
                                    disabled={categoryCurrentPage === 1}
                                />
                                {categoryCurrentPage > 2 && (
                                    <Pagination.Item onClick={() => handleCategoryPageChange(categoryCurrentPage - 2)}>
                                        {categoryCurrentPage - 2}
                                    </Pagination.Item>
                                )}
                                {categoryCurrentPage > 1 && (
                                    <Pagination.Item onClick={() => handleCategoryPageChange(categoryCurrentPage - 1)}>
                                        {categoryCurrentPage - 1}
                                    </Pagination.Item>
                                )}
                                <Pagination.Item active>{categoryCurrentPage}</Pagination.Item>
                                {categoryCurrentPage < categoryTotalPages && (
                                    <Pagination.Item onClick={() => handleCategoryPageChange(categoryCurrentPage + 1)}>
                                        {categoryCurrentPage + 1}
                                    </Pagination.Item>
                                )}
                                {categoryCurrentPage < categoryTotalPages - 1 && (
                                    <Pagination.Item onClick={() => handleCategoryPageChange(categoryCurrentPage + 2)}>
                                        {categoryCurrentPage + 2}
                                    </Pagination.Item>
                                )}
                                <Pagination.Next
                                    onClick={() => handleCategoryPageChange(categoryCurrentPage + 1)}
                                    disabled={categoryCurrentPage === categoryTotalPages}
                                />
                                <Pagination.Last
                                    onClick={() => handleCategoryPageChange(categoryTotalPages)}
                                    disabled={categoryCurrentPage === categoryTotalPages}
                                />
                            </Pagination>
                            <div className="ms-2">
                                <p>Mostrando {categoryCurrentNumberOfElements} de {categoryTotalElements}</p>
                            </div>
                        </Col>
                    </Row>
                </Container>

                <Row className="mt-3">
                    <Col className="text-center">
                        <Button
                            variant="primary"
                            onClick={() => setShowCategoryModal(true)}
                        >
                            Agregar Categoría
                        </Button>
                    </Col>
                </Row>
            </Container>

            {/* Sección de Gustos */}
            <Container className="mt-5">
                <Row className="mb-3">
                    <Col>
                        <h2 className="h4">Gustos</h2>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <input
                            type="text"
                            className="searchTerm"
                            placeholder="Buscar gusto..."
                            value={flavorSearchTerm}
                            onChange={(e) => setFlavorSearchTerm(e.target.value)}
                        />
                    </Col>
                    <Col md={4}>
                        <Form.Select
                            value={selectedCategoryId}
                            onChange={(e) => setSelectedCategoryId(e.target.value)}
                            className="form-control"
                        >
                            <option value="">Todas las categorías</option>
                            {categories.map((category) => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col md={4} className="text-end">
                        <Button variant="primary" className="me-2" onClick={handleFlavorSearch}>
                            <FaSearch />
                        </Button>
                        <Button variant="primary" onClick={handleFlavorClear}>
                            <FaTimes />
                        </Button>
                    </Col>
                </Row>

                <div className="table-responsive mt-3">
                    <Table responsive className="table-striped">
                        <thead>
                            <tr className="trTable">
                                <th className="text-center fw-bold" style={{
                                    backgroundColor: "rgb(235, 120, 107)",
                                    width: "25%",
                                    color: "#ffffff"
                                }}>Nombre</th>
                                <th className="text-center fw-bold" style={{
                                    backgroundColor: "rgb(235, 120, 107)",
                                    width: "30%",
                                    color: "#ffffff"
                                }}>Descripción</th>
                                <th className="text-center fw-bold" style={{
                                    backgroundColor: "rgb(235, 120, 107)",
                                    width: "20%",
                                    color: "#ffffff"
                                }}>Categoría</th>
                                <th className="text-center fw-bold" style={{
                                    backgroundColor: "rgb(235, 120, 107)",
                                    width: "10%",
                                    color: "#ffffff"
                                }}>Estado</th>
                                <th className="text-center fw-bold" style={{
                                    backgroundColor: "rgb(235, 120, 107)",
                                    width: "15%",
                                    color: "#ffffff"
                                }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {flavors.map((flavor) => (
                                <tr key={flavor.id}>
                                    <td className="text-center">{flavor.name}</td>
                                    <td className="text-center">{flavor.description || '-'}</td>
                                    <td className="text-center">{flavor.flavorCategoryName}</td>
                                    <td className="text-center">{flavor.status ? 'Activo' : 'Inactivo'}</td>
                                    <td>
                                        <div className="d-flex justify-content-center">
                                            <button
                                                className="btn btnEdit me-2"
                                                onClick={() => {
                                                    setSelectedFlavor(flavor);
                                                    setShowFlavorModal(true);
                                                }}
                                            >
                                                <RequestQuote size="24"/>
                                            </button>
                                            <button
                                                className="btn btnActiveInactive me-2"
                                                onClick={() => toggleFlavorStatus(flavor)}
                                            >
                                                {flavor.status ? (
                                                    <View size="24"/>
                                                ) : (
                                                    <ViewOff size="24"/>
                                                )}
                                            </button>
                                            <button
                                                className="btn btnDelete"
                                                onClick={() => handleDeleteFlavor(flavor)}
                                            >
                                                <TrashCan size="24"/>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>

                <Container fluid>
                    <Row>
                        <Col className="d-flex align-items-center justify-content-between">
                            <Pagination>
                                <Pagination.First
                                    onClick={() => handleFlavorPageChange(1)}
                                    disabled={flavorCurrentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => handleFlavorPageChange(flavorCurrentPage - 1)}
                                    disabled={flavorCurrentPage === 1}
                                />
                                {flavorCurrentPage > 2 && (
                                    <Pagination.Item onClick={() => handleFlavorPageChange(flavorCurrentPage - 2)}>
                                        {flavorCurrentPage - 2}
                                    </Pagination.Item>
                                )}
                                {flavorCurrentPage > 1 && (
                                    <Pagination.Item onClick={() => handleFlavorPageChange(flavorCurrentPage - 1)}>
                                        {flavorCurrentPage - 1}
                                    </Pagination.Item>
                                )}
                                <Pagination.Item active>{flavorCurrentPage}</Pagination.Item>
                                {flavorCurrentPage < flavorTotalPages && (
                                    <Pagination.Item onClick={() => handleFlavorPageChange(flavorCurrentPage + 1)}>
                                        {flavorCurrentPage + 1}
                                    </Pagination.Item>
                                )}
                                {flavorCurrentPage < flavorTotalPages - 1 && (
                                    <Pagination.Item onClick={() => handleFlavorPageChange(flavorCurrentPage + 2)}>
                                        {flavorCurrentPage + 2}
                                    </Pagination.Item>
                                )}
                                <Pagination.Next
                                    onClick={() => handleFlavorPageChange(flavorCurrentPage + 1)}
                                    disabled={flavorCurrentPage === flavorTotalPages}
                                />
                                <Pagination.Last
                                    onClick={() => handleFlavorPageChange(flavorTotalPages)}
                                    disabled={flavorCurrentPage === flavorTotalPages}
                                />
                            </Pagination>
                            <div className="ms-2">
                                <p>Mostrando {flavorCurrentNumberOfElements} de {flavorTotalElements}</p>
                            </div>
                        </Col>
                    </Row>
                </Container>

                <Row className="mt-3">
                    <Col className="text-center">
                        <Button
                            variant="primary"
                            onClick={() => setShowFlavorModal(true)}
                        >
                            Agregar Gusto
                        </Button>
                    </Col>
                </Row>
            </Container>

            {/* Modales */}
            <FlavorCategoryModal
                show={showCategoryModal}
                onHide={() => {
                    setShowCategoryModal(false);
                    setSelectedCategory(null);
                }}
                category={selectedCategory}
                onSuccess={loadCategories}
            />

            <FlavorModal
                show={showFlavorModal}
                onHide={() => {
                    setShowFlavorModal(false);
                    setSelectedFlavor(null);
                }}
                flavor={selectedFlavor}
                categories={categories}
                onSuccess={loadFlavors}
            />
        </div>
    );
};

export default FlavorManagement;